import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useShortcut } from "@/hooks/useShortcut";

interface Shortcut {
  keys: string[];
  label: string;
}

interface Section {
  title: string;
  items: Shortcut[];
}

const SECTIONS: Section[] = [
  {
    title: "General",
    items: [
      { keys: ["⌘", "K"], label: "Open command palette / search" },
      { keys: ["?"], label: "Show keyboard shortcuts" },
      { keys: ["Esc"], label: "Close overlays" },
    ],
  },
  {
    title: "Navigation",
    items: [
      { keys: ["g", "d"], label: "Go to Dashboard" },
      { keys: ["g", "r"], label: "Go to Reports" },
      { keys: ["g", "s"], label: "Go to Sites" },
      { keys: ["g", "i"], label: "Go to Inbox" },
    ],
  },
  {
    title: "Actions",
    items: [
      { keys: ["n"], label: "New item on current page" },
      { keys: ["/"], label: "Focus page search" },
    ],
  },
];

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[1.5rem] items-center justify-center rounded-md border border-border/70 bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] font-medium text-foreground shadow-sm">
      {children}
    </kbd>
  );
}

export default function ShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useShortcut({ key: "?", shift: true }, (e) => {
    e.preventDefault();
    setOpen((v) => !v);
  });
  // Also allow "/" without shift on some layouts
  useShortcut({ key: "/" }, (e) => {
    if (e.shiftKey) {
      e.preventDefault();
      setOpen((v) => !v);
    }
  });

  // Reset on route change
  useEffect(() => {
    const onPop = () => setOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Speed up common actions. Press{" "}
            <Kbd>?</Kbd> anytime to reopen this list.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </h3>
              <ul className="divide-y divide-border/50 rounded-lg border border-border/50">
                {section.items.map((s) => (
                  <li
                    key={s.label}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <span className="text-foreground/90">{s.label}</span>
                    <span className="flex items-center gap-1">
                      {s.keys.map((k, i) => (
                        <Kbd key={i}>{k}</Kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
